<template>
  <div
    class="app-root"
    :class="{
      small: containerWidth < 550,
    }"
  >
    <main class="map"></main>
  </div>
</template>

<style scoped>
.app-root {
  --map-width: v-bind(containerWidth + "px");

  width: 100%;
  height: 100%;

  display: flex;
  flex-direction: column;

  font-family: GT Eesti, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;

  font-size: 16px;
  line-height: 1;

  color: var(--text-color);
  background-color: var(--background-color);

  -webkit-tap-highlight-color: transparent;
}

.map {
  width: 100%;
  flex-grow: 1;
  position: relative;

  box-sizing: border-box;
  border: 3px solid var(--light);
}

@supports (mix-blend-mode: color-burn) {
  .map::after {
    content: "";

    pointer-events: none;

    position: absolute;
    z-index: 999;

    top: 0;
    left: 0;
    width: 100%;
    height: 100%;

    background-color: var(--gray);
    opacity: 0.3;
    mix-blend-mode: color-burn;
  }

  .dark-theme .map::after {
    opacity: 0.55;
  }
}
</style>

<script setup>
import { ref, computed, onMounted, onUnmounted, inject, nextTick } from "vue";

//
import jordaaniaGeoData from "./data/geo.json";

//
const containerElement = inject("containerElement");
const containerWidth = ref(0);

//
let map;

/**
 *
 */
async function initMap() {
  map = L.map(containerElement.querySelector(".map"), {
    onMapReady(map) {
      setTimeout(() => {
        map.invalidateSize(true);
      }, 1000);
    },
    zoomControl: false, // add manually
    minZoom: 3,
    // zoom: 6,
    // center: [48.594722, 38.000833],
  });

  const tileServer = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
  const attribution = `<a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>`;

  const tiles = L.tileLayer(tileServer, { attribution });
  tiles.addTo(map);

  L.control
    .zoom({
      position: "topleft",
    })
    .addTo(map);

  L.control.scale({ imperial: false }).addTo(map);

  // kontuur
  const jordaaniaOnMap = L.geoJSON(jordaaniaGeoData, {
    interactive: false,
    color: "#0064fa", // "rgb(0, 91, 187)",
    opacity: 0.55,
    weight: 3,
    fillOpacity: 0.01,
  }).addTo(map);

  // markers
  const cmOpts = {
    color: "#0064fa",
    weight: 3,
    radius: 7,
    opacity: 0.7,
    fillOpacity: 0.1,
  };

  const amman = L.circleMarker([31.953995, 35.910703], cmOpts).bindTooltip(
    "Amman",
    { direction: "right" }
  );

  const circleMarkers = [amman];

  circleMarkers.forEach((m) => {
    m.on("click", (e) => {
      const latlng = m.getLatLng();
      // console.log(latlng);
      map.setView(latlng, 11);

      nextTick(() => {
        m.openTooltip();
        window.setTimeout(map.invalidateSize, 500);
        window.setTimeout(map.invalidateSize, 1500);
      });
    });

    nextTick(() => {
      m.openTooltip();
    });
  });

  const markersGroup = L.featureGroup(circleMarkers);
  markersGroup.addTo(map);

  L.marker([31, 36.4], {
    interactive: false,
    icon: L.divIcon({
      className: "text-icon blue",
      html: "<div>Jordaania</div>",
      iconSize: null,
    }),
  }).addTo(map);

  L.marker([29, 39], {
    interactive: false,
    icon: L.divIcon({
      className: "text-icon",
      html: "<div>Saudi Araabia</div>",
      iconSize: null,
    }),
  }).addTo(map);

  L.marker([32.8, 41.2], {
    interactive: false,
    icon: L.divIcon({
      className: "text-icon",
      html: "<div>Iraak</div>",
      iconSize: null,
    }),
  }).addTo(map);

  L.marker([34, 38], {
    interactive: false,
    icon: L.divIcon({
      className: "text-icon",
      html: "<div>Süüria</div>",
      iconSize: null,
    }),
  }).addTo(map);

  L.marker([34.061245, 35.660372], {
    interactive: false,
    icon: L.divIcon({
      className: "text-icon",
      html: "<div>Liibanon</div>",
      iconSize: null,
    }),
  }).addTo(map);

  L.marker([32.076771, 34.834315], {
    interactive: false,
    icon: L.divIcon({
      className: "text-icon",
      html: "<div>Iisrael</div>",
      iconSize: null,
    }),
  }).addTo(map);

  //
  /* map.fitBounds([
    [45, 35.5],
    [50, 39],
  ]);*/
  map.fitBounds(jordaaniaOnMap.getBounds().pad(0.1));

  //
  await nextTick();

  //
  map.invalidateSize();
  window.setTimeout(map.invalidateSize, 1000);

  // open attribution links in new tab
  const links = containerElement.querySelectorAll("a");
  links.forEach((a) => {
    if (a.href.startsWith("http") && !a.href.includes("#")) {
      // console.log(a);
      a.setAttribute("target", "_blank");
    }
  });

  //
  /*map.on("click", (e) => {
    const { latlng } = e;
    const { lat, lng } = latlng;
    console.log(`${lat}, ${lng}`);
  });*/
}

function onResize() {
  containerWidth.value = containerElement.clientWidth;
}

// lifecycle
onMounted(() => {
  initMap();

  window.addEventListener("resize", onResize);
  onResize();
});

onUnmounted(() => {
  window.removeEventListener("resize", onResize);
});
</script>
